import { SearchOutlined } from "@ant-design/icons";
import type { TreeProps } from "antd";
import { Spin, Tree } from "antd";
import i18n from "i18next";
import type { FC } from "react";
import { useMemo, useState } from "react";

import { filterTreeNodes, getAllKeys, transformToTreeData } from "src/app/features/ConfigurationTransfer/configurationTransferUtils";
import { TextInput } from "../../../../../components/Form/formComponents";
import { NotificationType } from "../../../../../components/NotificationAlerts";
import { NotificationAlert } from "../../../../../components/NotificationAlerts/NotificationAlert";
import { Palette } from "../../../../../core/styles/palette";
import type { TransferableEntity } from "../../types/ConfigurationTransfer";

interface TransferableEntitiesTreeProps {
    /**
     * List of transferable entities from the API
     */
    entities: TransferableEntity[];

    /**
     * Currently checked tree node keys
     */
    checkedKeys: React.Key[];

    /**
     * Callback when tree selection changes
     */
    onCheck: TreeProps["onCheck"];

    /**
     * Whether the tree is disabled (e.g., when "Export All" is checked)
     */
    disabled?: boolean;

    /**
     * Loading state
     */
    loading?: boolean;
}

/**
 * TransferableEntitiesTree Component
 *
 * Displays transferable entities in a hierarchical tree structure with:
 * - Checkable nodes for selection
 * - Visual indicators for dependencies and children
 * - Search/filter functionality
 * - Disabled state support
 */
export const TransferableEntitiesTree: FC<TransferableEntitiesTreeProps> = ({
    entities,
    checkedKeys,
    onCheck,
    disabled = false,
    loading = false,
}) => {
    const [searchValue, setSearchValue] = useState("");
    const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
    const [autoExpandParent, setAutoExpandParent] = useState(true);

    // Transform entities to tree structure
    const treeData = useMemo(() => transformToTreeData(entities), [entities]);

    // Filter tree based on search
    const filteredTreeData = useMemo(() => filterTreeNodes(treeData, searchValue), [treeData, searchValue]);

    // Handle search input
    const handleSearch = (text: string) => {
        setSearchValue(text);

        if (text) {
            // Auto-expand all nodes when searching
            const allKeys = getAllKeys(treeData);
            setExpandedKeys(allKeys);
            setAutoExpandParent(true);
        } else {
            setAutoExpandParent(false);
        }
    };

    const handleExpand = (keys: React.Key[]) => {
        setExpandedKeys(keys);
        setAutoExpandParent(false);
    };

    if (entities.length === 0 && !loading) {
        return (
            <NotificationAlert titleKey={i18n.t("configurationTransfer.tree.noEntities")} type={NotificationType.INFO} description={""} />
        );
    }

    return (
        <Spin spinning={loading}>
            {/* Search Input */}
            <div style={{ marginBottom: 16 }}>
                <TextInput
                    placeholder={i18n.t("configurationTransfer.modal.searchPlaceholder")}
                    prefix={<SearchOutlined />}
                    onChange={handleSearch}
                    disabled={disabled}
                />
            </div>

            {/* Tree Component */}
            <Tree
                checkable
                checkStrictly={false} // Enable cascading selection
                disabled={disabled}
                expandedKeys={expandedKeys}
                autoExpandParent={autoExpandParent}
                onExpand={handleExpand}
                checkedKeys={checkedKeys}
                onCheck={onCheck}
                treeData={filteredTreeData}
                height={350}
                style={{
                    border: "1pt solid " + Palette.MidGray,
                    borderRadius: "4pt",
                    padding: "6pt 10pt",
                    backgroundColor: disabled ? Palette.DisabledBackground : Palette.White,
                    marginBottom: 16,
                }}
            />
        </Spin>
    );
};
